import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  GraduationCap, Clock, Users, Award, CheckCircle, Play, 
  BookOpen, Star, ChevronRight, Calendar, DollarSign, 
  Target, Zap, Shield, Download, X, Lock, Crown, Plus, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { useMembership } from '@/context/MembershipContext';
import { UpgradeModal } from '@/components/UpgradeModal';
import { getPhoneDisplay, getPhoneTel } from '@/lib/contactInfo';
import { PageEditProvider, usePageEditMode } from '@/context/PageEditModeContext';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { PageEditable } from '@/components/edit/PageEditable';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';
import { adminFetch } from '@/utils/adminAuth';
import { useAuth } from '@/context/AuthContext';
import { useHero } from '@/hooks/useHero';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Course Card Component
const CourseCard = ({ course, onEnroll, onPreview, onDelete }) => {
  const { enabled } = usePageEditMode();
  const { isAdmin } = useAuth();
  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : false;
  const p = `course.${course.id}`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-card border border-border/50 rounded-3xl overflow-hidden hover:border-primary/30 transition-all group relative"
    >
      {enabled && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(course); }}
          data-edit-safe
          data-testid={`delete-course-${course.id}`}
          className="absolute top-3 right-3 z-30 p-2 rounded-full bg-red-500/90 hover:bg-red-500 text-white shadow-lg"
          title="Delete this course"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
      {/* Course Image */}
      <div className="relative aspect-video overflow-hidden">
        <img loading="lazy" decoding="async" 
          src={course.image} 
          alt={course.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Level Badge */}
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
            course.level === 'Beginner' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
            course.level === 'Intermediate' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
            'bg-red-500/20 text-red-400 border border-red-500/30'
          }`}>
            <PageEditable path={`${p}.level`} as="span" fallback={course.level || ''} />
          </span>
        </div>

        {/* Play Button */}
        <button
          onClick={() => onPreview(course)}
          className="absolute bottom-4 right-4 w-12 h-12 rounded-full bg-primary flex items-center justify-center shadow-glow opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Play className="w-5 h-5 text-black ml-0.5" />
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl font-bold font-display text-foreground mb-2">
          <PageEditable path={`${p}.title`} as="span" fallback={course.title || ''} />
        </h3>
        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
          <PageEditable path={`${p}.description`} as="span" multiline fallback={course.description || ''} />
        </p>

        {/* Course Stats */}
        <div className="flex flex-wrap gap-4 mb-4 text-sm">
          <span className="flex items-center gap-1 text-muted-foreground">
            <Clock className="w-4 h-4 text-primary" />
            <PageEditable path={`${p}.duration`} as="span" fallback={course.duration || ''} />
          </span>
          <span className="flex items-center gap-1 text-muted-foreground">
            <BookOpen className="w-4 h-4 text-primary" />
            {course.lessons} <PageEditable path="cardLabels.lessons" as="span" fallback="lessons" />
          </span>
          <span className="flex items-center gap-1 text-muted-foreground">
            <Users className="w-4 h-4 text-primary" />
            {course.students} <PageEditable path="cardLabels.students" as="span" fallback="students" />
          </span>
        </div>

        {/* Price & CTA */}
        <div className="flex items-center justify-between pt-4 border-t border-border/30">
          <div>
            {isAdminUser ? (
              <span className="text-lg font-bold text-yellow-400">Included (Admin)</span>
            ) : (
              <>
                <span className="text-2xl font-bold text-primary">
                  $<PageEditable path={`${p}.price`} as="span" fallback={String(course.price || 0)} />
                </span>
                {course.originalPrice && (
                  <span className="text-sm text-muted-foreground line-through ml-2">${course.originalPrice}</span>
                )}
              </>
            )}
          </div>
          <button
            onClick={() => onEnroll(course)}
            className="px-5 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold text-sm transition-all hover:scale-105"
          >
            <PageEditable path="cardLabels.enrollBtn" as="span" fallback={isAdminUser ? 'Enter (Free)' : 'Enroll Now'} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Certification Card Component  
const CertificationCard = ({ cert, onEnroll, onDelete }) => {
  const { enabled } = usePageEditMode();
  const { isAdmin } = useAuth();
  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : false;
  const requirements = cert.requirements || [];
  const benefits = cert.benefits || [];
  const Icon = cert.icon || Award;
  const p = `certCard.${cert.id}`;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`bg-gradient-to-br ${cert.gradient || 'from-primary/10 to-accent/10'} border-2 ${cert.borderColor || 'border-primary/30'} rounded-3xl p-6 relative overflow-hidden`}
    >
      {/* Background Pattern */}
      <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
        <Award className="w-full h-full" />
      </div>

      {enabled && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(cert); }}
          data-edit-safe
          data-testid={`delete-cert-inline-${cert.id}`}
          className="absolute top-3 right-3 z-20 p-2 rounded-full bg-red-500/90 hover:bg-red-500 text-white shadow-lg"
          title="Delete this certificate"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}

      <div className="relative z-10">
        {/* Level Badge */}
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${cert.badgeColor || 'bg-primary/20 text-primary'} mb-4`}>
          <Icon className="w-5 h-5" />
          <span className="font-bold">
            <PageEditable path={`${p}.level`} as="span" fallback={cert.level || 'Certificate'} />
          </span>
        </div>

        <h3 className="text-2xl font-bold font-display text-foreground mb-2">
          <PageEditable path={`${p}.title`} as="span" fallback={cert.title || ''} />
        </h3>
        <p className="text-muted-foreground mb-4">
          <PageEditable path={`${p}.description`} as="span" multiline fallback={cert.description || ''} />
        </p>

        {/* Duration */}
        <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
          <Clock className="w-4 h-4 text-primary" />
          <PageEditable path={`${p}.duration`} as="span" fallback={cert.duration || 'Self-paced program'} />
        </div>

        {/* Requirements */}
        {requirements.length > 0 && (
          <div className="space-y-2 mb-6">
            <h4 className="font-bold text-foreground text-sm">
              <PageEditable path="certCardLabels.requirements" as="span" fallback="Requirements:" />
            </h4>
            {requirements.map((req, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                <PageEditable path={`${p}.requirements.${idx}`} as="span" multiline fallback={req} />
              </div>
            ))}
          </div>
        )}

        {/* Benefits */}
        {benefits.length > 0 && (
          <div className="space-y-2 mb-6">
            <h4 className="font-bold text-foreground text-sm">
              <PageEditable path="certCardLabels.benefits" as="span" fallback="You'll Receive:" />
            </h4>
            {benefits.map((benefit, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                <Star className="w-4 h-4 text-primary flex-shrink-0" />
                <PageEditable path={`${p}.benefits.${idx}`} as="span" multiline fallback={benefit} />
              </div>
            ))}
          </div>
        )}

        {/* Price & CTA */}
        <div className="pt-4 border-t border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div>
              {isAdminUser ? (
                <span className="text-2xl font-bold text-yellow-400">Included (Admin)</span>
              ) : (
                <>
                  <span className="text-3xl font-bold text-foreground">
                    $<PageEditable path={`${p}.price`} as="span" fallback={String(cert.price || 0)} />
                  </span>
                  <span className="text-sm text-muted-foreground ml-2">
                    <PageEditable path="certCardLabels.oneTime" as="span" fallback="one-time" />
                  </span>
                </>
              )}
            </div>
          </div>
          <button
            onClick={() => onEnroll(cert)}
            className={`w-full flex items-center justify-center gap-2 px-6 py-4 ${cert.buttonColor || 'bg-primary hover:bg-primary/90 text-black'} rounded-full font-bold transition-all hover:scale-105 shadow-glow`}
          >
            <Award className="w-5 h-5" />
            <PageEditable path="certCardLabels.enrollBtn" as="span" fallback="Enroll in Certification" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Main Courses Page
export const Courses = () => {
  const navigate = useNavigate();
  const { enabled } = usePageEditMode();
  const { isAdmin } = useAuth();
  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : false;
  const hero = useHero('courses');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedCert, setSelectedCert] = useState(null);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showCertModal, setShowCertModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [courses, setCourses] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(null); // 'course' | 'cert' | null
  const [newTitle, setNewTitle] = useState('');
  const [newPrice, setNewPrice] = useState('99');
  const [creating, setCreating] = useState(false);
  
  // Membership gating - courses require Silver tier
  const { canAccess } = useMembership();
  const hasAccess = canAccess('silver');

  const fetchData = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/courses/list/all`);
      const data = await response.json();
      const courseItems = data.filter(item => item.itemType === 'course' && item.id !== 'pandeiro-juggling-course');
      const certItems = data.filter(item => item.itemType === 'certificate');
      const certWithDefaults = certItems.map((cert, index) => ({
        ...cert,
        icon: index === 0 ? BookOpen : index === 1 ? Target : Shield,
        gradient: index === 0 ? 'from-green-500/10 to-emerald-500/10' : 
                  index === 1 ? 'from-blue-500/10 to-cyan-500/10' : 'from-primary/10 to-accent/10',
        borderColor: index === 0 ? 'border-green-500/30' : 
                     index === 1 ? 'border-blue-500/30' : 'border-primary/30',
        badgeColor: index === 0 ? 'bg-green-500/20 text-green-400' : 
                    index === 1 ? 'bg-blue-500/20 text-blue-400' : 'bg-primary/20 text-primary',
        buttonColor: index === 0 ? 'bg-green-500 hover:bg-green-600 text-white' : 
                     index === 1 ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-primary hover:bg-primary/90 text-black',
        level: cert.level ? `Level ${cert.level}` : `Level ${index + 1}`,
      }));
      setCourses(courseItems);
      setCertifications(certWithDefaults);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
      setCertifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async () => {
    const title = newTitle.trim();
    const type = showAddModal;
    if (!title) { toast.error('Enter a title'); return; }
    setCreating(true);
    try {
      const price = parseFloat(newPrice) || 0;
      const params = new URLSearchParams({ title, course_type: type === 'cert' ? 'certificate' : 'course' });
      const res = await adminFetch(`/admin/courses/create?${params.toString()}`, { method: 'POST' });
      if (!res.ok) throw new Error('Create failed');
      const data = await res.json();
      if (price !== 99) {
        await adminFetch(`/admin/courses/${data.course_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ price })
        });
      }
      toast.success(`${type === 'cert' ? 'Certificate' : 'Course'} created`);
      setShowAddModal(null);
      setNewTitle('');
      setNewPrice('99');
      await fetchData();
    } catch (e) {
      toast.error(e.message || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      const res = await adminFetch(`/admin/courses/${item.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Deleted');
      await fetchData();
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    }
  };

  // Default icons and colors for certifications (will be applied to DB data)
  const certIcons = [BookOpen, Target, Shield, Award, Star];
  const certGradients = [
    'from-green-500/10 to-emerald-500/10',
    'from-blue-500/10 to-cyan-500/10', 
    'from-primary/10 to-accent/10',
    'from-purple-500/10 to-pink-500/10',
    'from-amber-500/10 to-orange-500/10'
  ];
  const certBorderColors = [
    'border-green-500/30', 
    'border-blue-500/30', 
    'border-primary/30',
    'border-purple-500/30',
    'border-amber-500/30'
  ];
  const certBadgeColors = [
    'bg-green-500/20 text-green-400', 
    'bg-blue-500/20 text-blue-400', 
    'bg-primary/20 text-primary',
    'bg-purple-500/20 text-purple-400',
    'bg-amber-500/20 text-amber-400'
  ];
  const certButtonColors = [
    'bg-green-500 hover:bg-green-600 text-white', 
    'bg-blue-500 hover:bg-blue-600 text-white', 
    'bg-primary hover:bg-primary/90 text-black',
    'bg-purple-500 hover:bg-purple-600 text-white',
    'bg-amber-500 hover:bg-amber-600 text-black'
  ];

  const handleEnroll = (course) => {
    // Check membership - courses require Silver tier
    if (!hasAccess) {
      setShowUpgradeModal(true);
      return;
    }
    setSelectedCourse(course);
    setShowEnrollModal(true);
  };

  const handleCertEnroll = (cert) => {
    // Certifications also require Silver tier
    if (!hasAccess) {
      setShowUpgradeModal(true);
      return;
    }
    setSelectedCert(cert);
    setShowCertModal(true);
  };

  const handlePreview = (course) => {
    // Navigate to watch page for preview
    if (course.id) {
      navigate(`/watch/${course.id}`);
    } else {
      toast.info('Preview not available for this course');
    }
  };

  const handleCheckout = async (item, type) => {
    // Admin bypass — skip Stripe and grant simulated enrollment
    if (isAdminUser) {
      try {
        const orderId = `admin-course-${item.id}-${Date.now()}`;
        await fetch(`${BACKEND_URL}/api/orders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: orderId,
            type: 'admin_free',
            item_type: type === 'cert' ? 'certification' : 'course',
            item_id: item.id,
            item_title: item.title,
            amount: 0,
            list_price: item.price,
            status: 'enrolled',
            customer_email: 'admin@casasamba.com',
          }),
        }).catch(() => null);
        toast.success(`Admin free enrollment recorded — ${item.title}`);
        setShowEnrollModal(false);
        setShowCertModal(false);
        return;
      } catch (err) {
        toast.error('Admin free enrollment failed — see console');
        console.error(err);
        return;
      }
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/courses/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          course_id: item.id,
          course_name: type === 'cert' ? `${item.title} Certification` : item.title,
          price: item.price
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
      } else {
        toast.error('Failed to start checkout');
      }
    } catch (err) {
      toast.error('Checkout error. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5" />
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 rounded-full text-primary text-sm font-medium mb-6">
              <GraduationCap className="w-4 h-4" />
              <PageEditable path="hero.eyebrow" fallback={hero.kicker || "CASA SAMBA ACADEMY"} />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-display text-foreground mb-6">
              <PageEditable path="hero.title" fallback={hero.title_top || "Learn Afro-Brasilian"} /> <span className="text-primary"><PageEditable path="hero.titleAccent" fallback={hero.title_bottom || "Arts & Culture"} /></span>
            </h1>
            <PageEditable
              path="hero.subtitle"
              as="p"
              multiline
              className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8"
              fallback={hero.subtitle || "Master authentic Brasilian percussion, dance, and martial arts with Mestre Curtis Pierre. Take courses or pursue official certifications to become part of the global community."}
            />
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="#courses"
                className="inline-flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold text-lg transition-all hover:scale-105 shadow-glow"
              >
                <Play className="w-5 h-5" />
                <PageEditable path="hero.browseBtn" fallback="Browse Courses" />
              </a>
              <a 
                href="#certifications"
                className="inline-flex items-center gap-2 px-8 py-4 bg-card border border-border hover:border-primary/50 text-foreground rounded-full font-bold text-lg transition-all hover:scale-105"
              >
                <Award className="w-5 h-5" />
                <PageEditable path="hero.certifyBtn" fallback="Certifications" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-6 border-y border-border/30">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '38+', label: 'Years Teaching', key: 'years' },
              { value: '500+', label: 'Students Trained', key: 'students' },
              { value: '5', label: 'Course Programs', key: 'programs' },
              { value: '3', label: 'Certification Levels', key: 'certs' }
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-primary mb-1">
                  <PageEditable path={`stats.${stat.key}.value`} fallback={stat.value} />
                </div>
                <div className="text-sm text-muted-foreground">
                  <PageEditable path={`stats.${stat.key}.label`} fallback={stat.label} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED: Elite Pandeiro Masterclass */}
      <section className="py-16 px-6 bg-gradient-to-r from-yellow-500/10 via-amber-500/10 to-orange-500/10">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-3xl border-2 border-yellow-500/50 bg-card"
          >
            <div className="absolute top-0 right-0 w-1/2 h-full opacity-20">
              <img loading="lazy" decoding="async" 
                src="https://www.mestrecurtispierre.com/courses/wp-content/uploads/2020/04/1399.jpg"
                alt="Pandeiro Masterclass"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-card via-card/80 to-transparent" />
            </div>
            
            <div className="relative z-10 p-8 md:p-12">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-amber-600 text-black text-sm font-bold rounded-full">
                  <PageEditable path="pandeiro.badge_elite" as="span" fallback="ELITE MASTERCLASS" />
                </span>
                <span className="px-3 py-1 bg-white/10 text-foreground text-xs font-semibold rounded-full border border-white/20">
                  <PageEditable path="pandeiro.badge_rare" as="span" fallback="Only 3-4 Masters in USA" />
                </span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
                <PageEditable path="pandeiro.title" fallback="The Art of Pandeiro Juggling" />
              </h2>
              <p className="text-xl text-yellow-500 font-semibold mb-4">
                <PageEditable path="pandeiro.subtitle" fallback="Elite Masterclass by Mestre Curtis Pierre" />
              </p>
              <PageEditable
                path="pandeiro.body"
                as="p"
                multiline
                className="text-muted-foreground mb-6 max-w-xl"
                fallback="Master the rare art of Pandeiro juggling from one of only 3-4 masters in the United States. 47 HD lessons, 8+ hours of instruction, certificate of completion."
              />
              
              <div className="flex flex-wrap gap-6 mb-6 text-sm text-muted-foreground">
                <span className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-yellow-500" />
                  <PageEditable path="pandeiro.stat1" fallback="47 Lessons" />
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-yellow-500" />
                  <PageEditable path="pandeiro.stat2" fallback="8+ Hours" />
                </span>
                <span className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-yellow-500" />
                  <PageEditable path="pandeiro.stat3" fallback="Certificate" />
                </span>
                <span className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-yellow-500" />
                  <PageEditable path="pandeiro.stat4" fallback="Direct Support" />
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-6">
                <div>
                  <span className="text-4xl font-bold text-foreground">
                    <PageEditable path="pandeiro.price" fallback="$2,500" />
                  </span>
                  <span className="text-lg text-muted-foreground line-through ml-2">
                    <PageEditable path="pandeiro.priceOriginal" fallback="$3,500" />
                  </span>
                </div>
                <button
                  onClick={() => navigate('/pandeiro-juggling')}
                  className="px-8 py-4 bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-400 hover:to-amber-500 text-black rounded-full font-bold text-lg flex items-center gap-2 transition-all transform hover:scale-105"
                  data-testid="elite-masterclass-btn"
                >
                  <ChevronRight className="w-5 h-5" />
                  <PageEditable path="pandeiro.cta" fallback="Learn More" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Courses Section */}
      <section id="courses" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-display text-foreground mb-4">
              <PageEditable path="courses.heading" fallback="Courses" />
            </h2>
            <PageEditable
              path="courses.description"
              as="p"
              multiline
              className="text-muted-foreground max-w-2xl mx-auto"
              fallback="Individual training programs to learn specific Afro-Brasilian arts. Complete courses at your own pace with video lessons and live sessions."
            />
          </div>

          {/* $2 Test Purchase - Courses */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8 bg-gradient-to-r from-green-600/20 to-emerald-600/20 border-2 border-green-500/50 rounded-2xl p-6"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Zap className="w-8 h-8 text-green-400" />
                </div>
                <div>
                  <span className="text-green-400 font-semibold text-sm uppercase tracking-wider">
                    <PageEditable path="testCourse.eyebrow" as="span" fallback="Test Purchase" />
                  </span>
                  <h3 className="text-xl font-bold text-foreground">
                    <PageEditable path="testCourse.title" as="span" fallback="Test Course Checkout - $2.00" />
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    <PageEditable path="testCourse.description" as="span" multiline fallback="Verify the course purchase flow with a $2 test transaction" />
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleCheckout({ 
                  id: 'test-course-2', 
                  title: 'Test Course Purchase',
                  price: 2.00
                }, 'course')}
                className="px-8 py-3 bg-green-500 hover:bg-green-400 text-black rounded-full font-bold text-lg transition-all hover:scale-105"
                data-testid="test-buy-course-2"
              >
                <PageEditable path="testCourse.cta" as="span" fallback="Buy Test - $2.00" />
              </button>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <CourseCard 
                key={course.id} 
                course={course} 
                onEnroll={handleEnroll}
                onPreview={handlePreview}
                onDelete={handleDeleteItem}
              />
            ))}
            {enabled && (
              <button
                type="button"
                onClick={() => { setShowAddModal('course'); setNewTitle(''); setNewPrice('99'); }}
                data-edit-safe
                data-testid="add-course-btn"
                className="min-h-[380px] flex flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed border-primary/50 bg-primary/5 hover:bg-primary/10 hover:border-primary transition-all text-primary font-bold"
              >
                <Plus className="w-12 h-12" />
                <span className="text-lg">Add New Course</span>
                <span className="text-xs text-muted-foreground font-normal">Creates a fresh course template</span>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section id="certifications" className="py-20 px-6 bg-gradient-to-b from-background to-card">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 rounded-full text-primary text-sm font-medium mb-4">
              <Award className="w-4 h-4" />
              <PageEditable path="certifications.eyebrow" fallback="OFFICIAL CERTIFICATIONS" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-display text-foreground mb-4">
              <PageEditable path="certifications.heading" fallback="Certification Programs" />
            </h2>
            <PageEditable
              path="certifications.description"
              as="p"
              multiline
              className="text-muted-foreground max-w-2xl mx-auto"
              fallback="Comprehensive 6-12 month programs to become officially recognized within the global Afro-Brasilian cultural community. Earn your certificate and your identity."
            />
          </div>

          {/* $2 Test Purchase - Certifications */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border-2 border-purple-500/50 rounded-2xl p-6"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <Award className="w-8 h-8 text-purple-400" />
                </div>
                <div>
                  <span className="text-purple-400 font-semibold text-sm uppercase tracking-wider">
                    <PageEditable path="testCert.eyebrow" as="span" fallback="Test Purchase" />
                  </span>
                  <h3 className="text-xl font-bold text-foreground">
                    <PageEditable path="testCert.title" as="span" fallback="Test Certification Checkout - $2.00" />
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    <PageEditable path="testCert.description" as="span" multiline fallback="Verify the certification purchase flow with a $2 test transaction" />
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleCheckout({ 
                  id: 'test-cert-2', 
                  title: 'Test Certification',
                  price: 2.00
                }, 'cert')}
                className="px-8 py-3 bg-purple-500 hover:bg-purple-400 text-white rounded-full font-bold text-lg transition-all hover:scale-105"
                data-testid="test-buy-cert-2"
              >
                <PageEditable path="testCert.cta" as="span" fallback="Buy Test - $2.00" />
              </button>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {certifications.map((cert) => (
              <CertificationCard 
                key={cert.id} 
                cert={cert} 
                onEnroll={handleCertEnroll}
                onDelete={handleDeleteItem}
              />
            ))}
            {enabled && (
              <button
                type="button"
                onClick={() => { setShowAddModal('cert'); setNewTitle(''); setNewPrice('500'); }}
                data-edit-safe
                data-testid="add-cert-btn"
                className="min-h-[420px] flex flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed border-primary/50 bg-primary/5 hover:bg-primary/10 hover:border-primary transition-all text-primary font-bold"
              >
                <Plus className="w-12 h-12" />
                <span className="text-lg">Add New Certificate</span>
                <span className="text-xs text-muted-foreground font-normal">Creates a fresh certificate template</span>
              </button>
            )}
          </div>

          {/* Certification Path Visual */}
          <div className="hidden md:flex items-center justify-center mt-12">
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-green-500/20 border-2 border-green-500/30 flex items-center justify-center mx-auto">
                  <BookOpen className="w-6 h-6 text-green-400" />
                </div>
                <p className="text-sm text-muted-foreground mt-2">6-8 months</p>
                <p className="text-sm font-bold text-green-400">$500</p>
              </div>
              <ChevronRight className="w-6 h-6 text-muted-foreground" />
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-blue-500/20 border-2 border-blue-500/30 flex items-center justify-center mx-auto">
                  <Target className="w-6 h-6 text-blue-400" />
                </div>
                <p className="text-sm text-muted-foreground mt-2">8-12 months</p>
                <p className="text-sm font-bold text-blue-400">$1,000</p>
              </div>
              <ChevronRight className="w-6 h-6 text-muted-foreground" />
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary/20 border-2 border-primary/30 flex items-center justify-center mx-auto">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground mt-2">12+ months</p>
                <p className="text-sm font-bold text-primary">$1,500</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-primary/20 to-accent/20 border border-primary/30 rounded-3xl p-8 md:p-12 text-center"
          >
            <GraduationCap className="w-16 h-16 text-primary mx-auto mb-6" />
            <h2 className="text-3xl font-bold font-display text-foreground mb-4">
              <PageEditable path="cta.heading" fallback="Not Sure Where to Start?" />
            </h2>
            <PageEditable
              path="cta.description"
              as="p"
              multiline
              className="text-muted-foreground mb-8 max-w-xl mx-auto"
              fallback="Contact us for a free consultation. We'll help you choose the right course or certification path based on your goals and experience level."
            />
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={getPhoneTel()}
                className="px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold transition-all hover:scale-105 shadow-glow"
              >
                <PageEditable path="cta.callPrefix" as="span" fallback="Call:" /> {getPhoneDisplay()}
              </a>
              <a
                href="mailto:info@casasamba.com"
                className="px-8 py-4 bg-card border border-border hover:border-primary/50 text-foreground rounded-full font-bold transition-all hover:scale-105"
              >
                <PageEditable path="cta.emailBtn" as="span" fallback="Email Us" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Course Enroll Modal */}
      <AnimatePresence>
        {showEnrollModal && selectedCourse && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowEnrollModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card border border-border/50 rounded-3xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold font-display text-foreground">{selectedCourse.title}</h2>
                  <p className="text-muted-foreground">with {selectedCourse.instructor}</p>
                </div>
                <button
                  onClick={() => setShowEnrollModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Course Details */}
              <div className="space-y-6">
                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-background rounded-xl p-4 text-center">
                    <Clock className="w-6 h-6 text-primary mx-auto mb-2" />
                    <div className="font-bold text-foreground">{selectedCourse.duration}</div>
                    <div className="text-xs text-muted-foreground">Duration</div>
                  </div>
                  <div className="bg-background rounded-xl p-4 text-center">
                    <BookOpen className="w-6 h-6 text-primary mx-auto mb-2" />
                    <div className="font-bold text-foreground">{selectedCourse.lessons}</div>
                    <div className="text-xs text-muted-foreground">Lessons</div>
                  </div>
                  <div className="bg-background rounded-xl p-4 text-center">
                    <Users className="w-6 h-6 text-primary mx-auto mb-2" />
                    <div className="font-bold text-foreground">{selectedCourse.students}</div>
                    <div className="text-xs text-muted-foreground">Students</div>
                  </div>
                </div>

                {/* Curriculum */}
                <div>
                  <h3 className="font-bold text-foreground mb-3">Curriculum</h3>
                  <div className="space-y-2">
                    {selectedCourse.curriculum.map((item) => (
                      <div key={item.chapter} className="flex items-start gap-3 p-3 bg-background rounded-xl">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">
                          {item.chapter}
                        </div>
                        <div>
                          <div className="font-medium text-foreground">{item.title}</div>
                          <div className="text-sm text-muted-foreground">{item.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* What You'll Learn */}
                <div>
                  <h3 className="font-bold text-foreground mb-3">What You'll Learn</h3>
                  <div className="space-y-2">
                    {selectedCourse.whatYouLearn.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Requirements */}
                <div>
                  <h3 className="font-bold text-foreground mb-3">Requirements</h3>
                  <div className="space-y-2">
                    {selectedCourse.requirements.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Zap className="w-4 h-4 text-primary flex-shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Price & Enroll */}
                <div className="flex items-center justify-between pt-6 border-t border-border/30">
                  <div>
                    <span className="text-3xl font-bold text-primary">${selectedCourse.price}</span>
                    {selectedCourse.originalPrice && (
                      <span className="text-muted-foreground line-through ml-2">${selectedCourse.originalPrice}</span>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">Course only (certification separate)</p>
                  </div>
                  <button
                    onClick={() => handleCheckout(selectedCourse, 'course')}
                    className="flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold text-lg transition-all hover:scale-105 shadow-glow"
                  >
                    <DollarSign className="w-5 h-5" />
                    Enroll Now
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Certification Enroll Modal */}
      <AnimatePresence>
        {showCertModal && selectedCert && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCertModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card border border-border/50 rounded-3xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${selectedCert.badgeColor} text-sm font-bold mb-2`}>
                    <selectedCert.icon className="w-4 h-4" />
                    {selectedCert.level}
                  </div>
                  <h2 className="text-2xl font-bold font-display text-foreground">{selectedCert.title}</h2>
                  <p className="text-muted-foreground">{selectedCert.duration}</p>
                </div>
                <button
                  onClick={() => setShowCertModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Cert Details */}
              <div className="space-y-6">
                <p className="text-muted-foreground">{selectedCert.description}</p>

                {/* Program Curriculum */}
                <div>
                  <h3 className="font-bold text-foreground mb-3">Program Curriculum</h3>
                  <div className="space-y-2">
                    {selectedCert.curriculum.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-background rounded-xl">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">
                          {idx + 1}
                        </div>
                        <span className="text-foreground">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Requirements */}
                <div>
                  <h3 className="font-bold text-foreground mb-3">Requirements</h3>
                  <div className="space-y-2">
                    {selectedCert.requirements.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Benefits */}
                <div>
                  <h3 className="font-bold text-foreground mb-3">What You'll Receive</h3>
                  <div className="space-y-2">
                    {selectedCert.benefits.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Star className="w-4 h-4 text-primary flex-shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Price & Enroll */}
                <div className="flex items-center justify-between pt-6 border-t border-border/30">
                  <div>
                    <span className="text-3xl font-bold text-foreground">${selectedCert.price}</span>
                    <p className="text-xs text-muted-foreground mt-1">One-time certification fee</p>
                  </div>
                  <button
                    onClick={() => handleCheckout(selectedCert, 'cert')}
                    className={`flex items-center gap-2 px-8 py-4 ${selectedCert.buttonColor} rounded-full font-bold text-lg transition-all hover:scale-105 shadow-glow`}
                  >
                    <Award className="w-5 h-5" />
                    Enroll Now
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Add Course/Cert Modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70"
          onClick={() => !creating && setShowAddModal(null)}
          data-edit-safe
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            data-edit-safe
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">
                {showAddModal === 'cert' ? 'Add New Certificate' : 'Add New Course'}
              </h3>
              <button
                type="button"
                onClick={() => !creating && setShowAddModal(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Title *</label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder={showAddModal === 'cert' ? 'e.g. Level 4 Master Certification' : 'e.g. Berimbau Fundamentals'}
                  data-testid="add-item-title-input"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Price (USD)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  data-testid="add-item-price-input"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                A blank template will be created. Edit every field inline on the card afterwards.
              </p>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => !creating && setShowAddModal(null)}
                className="flex-1 py-3 bg-muted hover:bg-muted/80 rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={creating || !newTitle.trim()}
                data-testid="create-item-btn"
                className="flex-1 py-3 bg-primary hover:bg-primary/90 text-black rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                {creating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Upgrade Modal - Courses require Silver tier */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        requiredTier="silver"
        contentTitle="Escola de Samba Casa Samba Courses"
      />
    </div>
  );
};

const CoursesWithEdit = () => (
  <PageEditProvider slug="courses">
    <Courses />
    <PageEditToolbar />
    <AdminEditorLink to="/admin/dashboard#course-editor-btn" label="Edit Courses" />
  </PageEditProvider>
);

export default CoursesWithEdit;
